import { useMemo } from "react";
import { useReplaceCurrentSearchTerm } from "@sonder/editor/source/core/components/Mention";
import { useSearchTerm } from "@sonder/editor/source/core/components/SearchTerm";
import {
  getHtmlFromState,
  getInitialEditorState,
  getUserMentionIds,
} from "@sonder/editor/source/core/helpers";
import { useEditorStore } from "@sonder/editor/source/core/stores";
import { useSearchTermStore } from "@sonder/editor/source/core/stores/SearchTermStore";
import { EditorProps } from "@sonder/editor/typings/editor";

export type ExternalStateBridgeProps = Partial<Pick<EditorProps, "children">>;
export const ExternalStateBridge = ({ children }: ExternalStateBridgeProps) => {
  // onContentChange handler
  const { state: editorState, setState: setEditorState } = useEditorStore();

  // onSearchTermChange handler
  const { state: searchTermState } = useSearchTermStore();
  const { searchTerm, position } = searchTermState;
  const { cancelSearchTerm } = useSearchTerm();
  const replaceCurrentSearchTerm = useReplaceCurrentSearchTerm();

  return useMemo(() => {
    if (typeof children === "function") {
      const editorContent = editorState.getCurrentContent();
      return children({
        // Content state
        htmlBody: getHtmlFromState(editorState),
        plainBody: editorContent.getPlainText(),
        mentions: getUserMentionIds(editorContent),

        // Search term state
        searchTerm,
        searchTermPosition: position,
        applyUserMention: ({ name, userId }) => {
          replaceCurrentSearchTerm({
            type: "USER_MENTION",
            text: name,
            mutability: "IMMUTABLE",
            data: { userId },
          });
        },
        cancelSearchTerm,
        clearEditorState: () => {
          cancelSearchTerm();
          setEditorState(getInitialEditorState({}));
        },
      });
    }
    return null;
  }, [
    children,
    editorState,
    searchTerm,
    position,
    replaceCurrentSearchTerm,
    cancelSearchTerm,
  ]);
};
